import React, { useState } from 'react';

import { startCase } from 'lodash';

import { Panel } from '@shared/components/bootstrap';
import { Lightbox } from '@shared/components/helpers/lightbox';
import { Picture } from '@shared/components/helpers/picture';
import { ImgixBG, ImgixFit } from '@shared/utils/imgix';

import { ItemBrick } from '@admin/components/items/item_brick';
import { Pagination } from '@admin/components/pagination';
import { QaQrCodeLink } from '@admin/components/qa_automation/qa_qr_code_link';
import { Spinner } from '@admin/components/spinner';
import { accountURL, locationURL, palletURL } from '@admin/config/routes';

import { accountIDsForPallet, accountIDsForPallets, useCustomersRequest, useLocationPalletsRequest } from './api';
import { LocationPallet, LocationPalletCustomer } from './types';

const DENSITY_TYPES = new Set(['Single', 'Double', 'Stackable Crate']);

/** JSON may encode BigDecimal percentage as a string (`encode_big_decimal_as_string`). */
const parseDensityPercentage = (raw: unknown): number | undefined => {
  if (raw == null) return undefined;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
};

const gradeLabelClass = (score: string): string => {
  switch (score.toLowerCase()) {
    case 'good':
      return 'label-success';
    case 'acceptable':
      return 'label-warning';
    case 'fail':
      return 'label-danger';
    default:
      return 'label-default';
  }
};

const LocationLink: React.FC<{ location: NonNullable<LocationPallet['location']> }> = ({ location }) => (
  <a href={locationURL(location.id)} className="text-primary">
    #{location.value}
  </a>
);

const PalletCard: React.FC<{
  pallet: LocationPallet;
  customers: LocationPalletCustomer[];
}> = ({ pallet, customers }) => {
  const [gradeLightboxUrl, setGradeLightboxUrl] = useState<string | undefined>();
  const showHeightLabel = pallet.size !== 'oversized' && Boolean(pallet.height);
  const showAccounts = Boolean(pallet.location) || pallet.state === 'enroute';
  const densityPercentage = parseDensityPercentage(pallet.density_metrics?.percentage);
  const showDensity = DENSITY_TYPES.has(pallet.type.name) && densityPercentage !== undefined;

  if (pallet.items_count <= 0) {
    return null;
  }

  return (
    <div>
      {gradeLightboxUrl && <Lightbox imageURL={gradeLightboxUrl} onClose={() => setGradeLightboxUrl(undefined)} />}

      <Panel>
        <Panel.Body>
          <div className="pull-left">
            {pallet.grade?.image && (
              <div className="pull-left panel-pallet-image">
                <button
                  type="button"
                  className="btn-link"
                  style={{ padding: 0, border: 'none', background: 'none' }}
                  aria-label="View pallet grade image"
                  onClick={() => setGradeLightboxUrl(pallet.grade?.image?.source)}
                >
                  <figure>
                    <Picture
                      source={pallet.grade.image.source}
                      width={50}
                      height={50}
                      fit={ImgixFit.Fill}
                      background={ImgixBG.White}
                      alt=""
                    />
                  </figure>
                </button>
              </div>
            )}
            <div className="pull-right">
              <a className="btn-link" href={palletURL(pallet.id)}>
                #{pallet.barcode.value}
              </a>{' '}
              <strong>({startCase(pallet.state)})</strong>{' '}
              {pallet.grade?.score && (
                <span className={`label ${gradeLabelClass(pallet.grade.score)}`}>
                  {startCase(pallet.grade.score.toLowerCase())}
                </span>
              )}
              <QaQrCodeLink value={pallet.barcode.value} />
              <br />
              <span>
                {pallet.type.name} ({pallet.items_count} {pallet.items_count === 1 ? 'Item' : 'Items'})
              </span>{' '}
              {showDensity && (
                <span className={`label ${densityPercentage >= 75 ? 'label-success' : 'label-danger'}`}>
                  Density: {densityPercentage.toFixed(2)}%
                </span>
              )}{' '}
              {showHeightLabel && pallet.height && (
                <span className="label label-default">Height: {pallet.height.value} in.</span>
              )}
            </div>
          </div>
          <div className="pull-right">
            {showAccounts && customers.length > 0 && (
              <div>
                <strong>{customers.length === 1 ? 'Account:' : 'Accounts:'}</strong>{' '}
                {customers.map((customer, index) => (
                  <span key={customer.id}>
                    {index > 0 && ', '}
                    <a className="btn-link" href={accountURL({ id: customer.account_id })}>
                      {customer.name}
                    </a>
                  </span>
                ))}
              </div>
            )}
            {pallet.location ? (
              <>
                <div>
                  <strong>{startCase(pallet.location.located.type)}:</strong>{' '}
                  <span>{pallet.location.located.object.name}</span>
                </div>
                <div>
                  <strong>Location:</strong> <LocationLink location={pallet.location} />{' '}
                  <QaQrCodeLink value={pallet.location.value} />
                </div>
              </>
            ) : (
              <div>
                <strong>Location:</strong> <span>On Transfer Truck</span>
              </div>
            )}
          </div>
        </Panel.Body>
      </Panel>

      <div className="grid">
        <div className="row">
          {pallet.items.map((item) => (
            <div key={item.id} className="entry col-xs-6 col-sm-4 col-md-3 col-lg-2">
              <ItemBrick item={{ ...item, accountID: item.account_id }} sizeable />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const LocationPalletsList: React.FC<{ locationID: string }> = ({ locationID }) => {
  const [page, setPage] = useState(1);

  const { loading: loadingLocationPalletsRequest, response: responseLocationPalletsRequest } =
    useLocationPalletsRequest(locationID, page);

  const accountIDs = Array.from(accountIDsForPallets(responseLocationPalletsRequest?.data.results ?? []));

  const { loading: loadingCustomersRequest, response: responseCustomersRequest } = useCustomersRequest(accountIDs);

  const loading = loadingLocationPalletsRequest || loadingCustomersRequest;

  if (loading) {
    return <Spinner />;
  }

  const pagination = responseLocationPalletsRequest?.data.pagination;
  const pallets = responseLocationPalletsRequest?.data.results ?? [];
  const customers = (responseCustomersRequest?.data ?? []).filter((customer) => customer.primary);

  if (!pallets.length) {
    return null;
  }

  const customersForPallet = (pallet: LocationPallet) => {
    const palletAccountIDs = accountIDsForPallet(pallet);
    return customers.filter((customer) => palletAccountIDs.has(customer.account_id));
  };

  return (
    <>
      {pallets.map((pallet) => (
        <PalletCard key={pallet.id} pallet={pallet} customers={customersForPallet(pallet)} />
      ))}
      {pagination && <Pagination pagination={pagination} onPage={setPage} />}
    </>
  );
};
